import { useMemo, useState } from "react";
import { AlertOctagon, CheckCircle2, ExternalLink, FileText, Link2, Search } from "lucide-react";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../ui/sheet";
import type { AmlBucket, AmlMatch, AmlScreeningResult, AmlSeverity } from "../../lib/aml/types";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  directorName: string;
  result: AmlScreeningResult | null;
  loading?: boolean;
  /** Restrict the panel to one screening column. */
  bucket?: AmlBucket;
};

const severityClass: Record<AmlSeverity, string> = {
  high: "bg-red-100 text-red-700",
  medium: "bg-orange-100 text-orange-700",
  low: "bg-muted text-muted-foreground",
};

const Field = ({ label, value }: { label: string; value: string | null }) =>
  value ? (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 break-words text-sm font-medium">{value}</dd>
    </div>
  ) : null;

const Stat = ({ label, value, tone }: { label: string; value: string | number; tone?: string }) => (
  <div className="rounded-lg border bg-card px-3 py-2">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className={`text-lg font-semibold ${tone ?? ""}`}>{value}</p>
  </div>
);

function MatchDetails({ match }: { match: AmlMatch }) {
  const links = [
    { label: "Evidence record", href: match.evidenceLink, icon: Link2 },
    { label: "Source page", href: match.sourceUrl ?? match.evidenceUrl, icon: ExternalLink },
    { label: "Official PDF", href: match.pdfUrl, icon: FileText },
    { label: "Full case report", href: match.caseDetailsLink, icon: ExternalLink },
  ].filter((link) => Boolean(link.href));

  return (
    <div className="space-y-5">
      <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <Field label="Matched name" value={match.name} />
        <Field label="Alias" value={match.alias} />
        <Field label="Father's name" value={match.fatherName} />
        <Field label="Date of birth" value={match.dob ?? match.birthYear} />
        <Field label="Gender" value={match.gender} />
        <Field label="Nationality" value={match.nationality} />
        <Field label="Address" value={match.address} />
        <Field label="Place of birth" value={match.placeOfBirth} />
        <Field label="PAN" value={match.panNumber} />
        <Field label="Phone" value={match.phone} />
        <Field label="Email" value={match.email} />
        <Field label="Crime type" value={match.crimeType} />
      </dl>

      <div className="rounded-md border bg-muted/30 p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Source & match quality
        </p>
        <dl className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-3">
          <Field label="Source" value={match.source} />
          <Field label="Source type" value={match.sourceType} />
          <Field label="Sub source" value={match.subSourceType} />
          <Field label="Country" value={match.sourceCountry} />
          <Field label="Region" value={match.sourceContinent} />
          <Field label="Category" value={match.sourceCategory} />
          <Field label="Match type" value={match.matchType} />
          <Field label="Score" value={match.score === null ? null : `${match.score}`} />
          <Field label="Name match" value={match.nameMatchType} />
          <Field label="DOB match" value={match.dobMatchType} />
          <Field label="Father match" value={match.fatherMatchType} />
          <Field label="Address match" value={match.addressMatchType} />
        </dl>
        {match.subCategories.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {match.subCategories.map((item) => (
              <Badge key={item} variant="outline" className="text-xs font-normal">
                {item}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {(match.amount ||
        match.outstandingAmount ||
        match.companyName ||
        match.borrowerName ||
        match.bankName ||
        match.bankBranch ||
        match.otherBank ||
        match.orderDate ||
        match.year ||
        match.directorsInfo ||
        match.directorsDinInfo) && (
        <div className="rounded-md border p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Case particulars
          </p>
          <dl className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-3">
            <Field label="Amount involved" value={match.amount} />
            <Field label="Outstanding amount" value={match.outstandingAmount} />
            <Field label="Company" value={match.companyName} />
            <Field label="Borrower" value={match.borrowerName} />
            <Field label="Bank" value={match.bankName} />
            <Field label="Branch" value={match.bankBranch} />
            <Field label="Other banks" value={match.otherBank} />
            <Field label="Order date" value={match.orderDate} />
            <Field label="Reported year" value={match.year} />
            <Field label="Directors" value={match.directorsInfo} />
            <Field label="Director DINs" value={match.directorsDinInfo} />
          </dl>
        </div>
      )}

      {(match.summary || match.description) && (
        <div className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Narrative{match.pressReleaseDate ? ` • ${match.pressReleaseDate}` : ""}
          </p>
          {match.summary && <p className="text-sm leading-6">{match.summary}</p>}
          {match.description && match.description !== match.summary && (
            <p className="text-sm leading-6 text-muted-foreground">{match.description}</p>
          )}
        </div>
      )}

      {links.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {links.map(({ label, href, icon: Icon }) => (
            <Button key={label} asChild size="sm" variant="outline">
              <a href={href as string} target="_blank" rel="noreferrer">
                <Icon /> {label}
              </a>
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Slide-over listing every AML hit for an entity, grouped by source and expandable to full detail. */
export function AmlPanel({
  open,
  onOpenChange,
  directorName,
  result,
  loading,
  bucket,
}: Props) {
  const [query, setQuery] = useState("");

  const matches = useMemo(() => {
    if (!result) return [];
    const term = query.trim().toLowerCase();
    const byBucket = bucket
      ? result.matches.filter((match) => match.bucket === bucket)
      : result.matches;
    // If column filter yields nothing but the entity has hits, show all (bucket tagging can differ from API counts).
    const scoped =
      bucket && byBucket.length === 0 && result.matches.length > 0
        ? result.matches
        : byBucket;
    return scoped.filter(
      (match) =>
        !term ||
        match.name.toLowerCase().includes(term) ||
        match.source.toLowerCase().includes(term) ||
        (match.alias ?? "").toLowerCase().includes(term)
    );
  }, [result, query, bucket]);

  const groups = useMemo(() => {
    const bySource = new Map<string, AmlMatch[]>();
    for (const match of matches) {
      bySource.set(match.source, [...(bySource.get(match.source) ?? []), match]);
    }
    return [...bySource.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [matches]);

  const confirmed = matches.filter((match) => match.matchType?.toUpperCase() === "EXACT").length;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto p-0 sm:max-w-3xl">
        <SheetHeader className="border-b bg-muted/40 px-6 py-5 pr-14">
          <SheetTitle className="flex items-center gap-2">
            AML Screening
            {loading ? (
              <Badge variant="secondary">Loading…</Badge>
            ) : matches.length > 0 ? (
              <Badge variant="secondary" className="bg-red-100 text-red-700">
                <AlertOctagon className="size-3.5" /> {matches.length} matches
              </Badge>
            ) : (
              <Badge variant="secondary" className="bg-green-100 text-green-700">
                <CheckCircle2 className="size-3.5" /> Clear
              </Badge>
            )}
          </SheetTitle>
          <SheetDescription>
            {directorName}
            {result ? ` • screened ${new Date(result.screenedAt).toLocaleString("en-IN")}` : ""}
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="px-6 py-16 text-center text-sm text-muted-foreground">
            Loading AML response…
          </div>
        ) : matches.length === 0 && !query ? (
          <div className="px-6 py-16 text-center">
            <span className="mx-auto flex size-14 items-center justify-center rounded-full bg-green-100 text-green-700">
              <CheckCircle2 className="size-6" />
            </span>
            <p className="mt-4 text-lg font-semibold">No adverse records found</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {directorName} did not match any sanctions, enforcement or defaulter list in this
              screening run.
            </p>
          </div>
        ) : (
          <div className="space-y-5 px-6 py-5">
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Confirmed" value={confirmed} tone="text-red-700" />
              <Stat label="Probable" value={matches.length - confirmed} tone="text-orange-600" />
              <Stat label="Sources" value={groups.length} />
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search by name or source"
                className="pl-9"
              />
            </div>

            {groups.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No records match “{query}”.
              </p>
            ) : (
              <Accordion type="multiple" defaultValue={[groups[0]![0]]} className="space-y-3">
                {groups.map(([source, items]) => (
                  <AccordionItem key={source} value={source} className="rounded-lg border px-4">
                    <AccordionTrigger>
                      <span className="flex min-w-0 flex-1 items-center gap-2 pr-3 text-left">
                        <span className="min-w-0 truncate font-medium">{source}</span>
                        <Badge variant="secondary">{items.length}</Badge>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <Accordion type="multiple" className="border-t">
                        {items.map((match) => (
                          <AccordionItem key={match.caseId} value={match.caseId}>
                            <AccordionTrigger>
                              <span className="flex min-w-0 flex-1 flex-wrap items-center gap-2 pr-3 text-left">
                                <Badge
                                  variant="secondary"
                                  className={severityClass[match.severity]}
                                >
                                  {match.matchType ?? "MATCH"}
                                  {match.score === null ? "" : ` • ${match.score}`}
                                </Badge>
                                <span className="min-w-0 truncate font-medium">{match.name}</span>
                                <span className="truncate text-xs text-muted-foreground">
                                  {[match.sourceCountry, match.amount ?? match.outstandingAmount]
                                    .filter(Boolean)
                                    .join(" • ")}
                                </span>
                              </span>
                            </AccordionTrigger>
                            <AccordionContent>
                              <MatchDetails match={match} />
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
